import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAxios } from '../core/_axios';
import usePageTitle from '../hooks/usePageTitle';
import useAccount from '../hooks/useAccount';
import useAccountChannel from '../hooks/useAccountChannel';
import OnboardingFlow from '../components/onboarding/OnboardingFlow';
import { usePageHeader } from '../contexts/PageHeaderContext';
import QRCode from 'react-qr-code';
import {
  Sparkles,
  CheckCircle2,
  Circle,
  Tablet,
  UserPlus,
  FileText,
  ShoppingCart,
  Book,
  Mail,
  Settings,
  ChevronRight,
  Gift,
  ArrowRight,
  Zap,
  Star,
  Heart,
  PenTool,
  QrCode,
  X
} from 'lucide-react';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
  action: {
    label: string;
    path?: string;
    onClick?: () => void;
    disabled?: boolean;
  };
  category: 'setup' | 'service' | 'advanced';
}

const Welcome: React.FC = () => {
  usePageTitle('Welcome');
  const { currentUser, viewingAllAccounts, tryToVerifyToken } = useAuth();
  const axios = useAxios();
  const navigate = useNavigate();
  const location = useLocation();
  const { account, onLoadAccount } = useAccount({ axios, isForCurrentAccount: true });
  const { setPageHeader } = usePageHeader();
  
  // Redirect if viewing all accounts
  if (viewingAllAccounts) {
    return <Navigate to="/signatures/list" replace />;
  }
  
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [stats, setStats] = useState({
    services: 0,
    signatures: 0,
    ipads: 0
  });
  const [showQRModal, setShowQRModal] = useState(false);
  const [firstService, setFirstService] = useState<any>(null);
  const hasCheckedOnboarding = useRef(false);
  const [localUserData, setLocalUserData] = useState<any>(null);

  // Define loadStats early so it can be used in effects
  const loadStats = useCallback(async () => {
    try {
      const [servicesRes, signaturesRes, ipadsRes] = await Promise.all([
        axios.get('/api/v1/app/authenticated/services'),
        axios.get('/api/v1/app/authenticated/signatures'),
        axios.get('/api/v1/app/authenticated/ipads')
      ]);
      
      // The services endpoint returns data wrapped in a 'data' object
      const services = servicesRes.data.data?.services || servicesRes.data.services || [];
      const signatures = signaturesRes.data.data?.signatures || signaturesRes.data.signatures || [];
      const ipads = ipadsRes.data.data?.ipads || ipadsRes.data.ipads || [];
      
      // Store the first service for QR code modal
      if (services.length > 0) {
        setFirstService(services[0]);
      }
      
      setStats({
        services: services.length,
        signatures: signatures.length,
        ipads: ipads.length
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, [axios]);

  // Set up WebSocket for real-time updates
  useAccountChannel({
    onSignatureUpdated: useCallback((reason, signature) => {
      console.log('[Welcome] Signature updated via WebSocket:', reason);
      // When a signature is created or updated, reload stats to update the count
      loadStats();
    }, [loadStats]),
    onIpadUpdated: useCallback((ipad) => {
      console.log('[Welcome] iPad updated via WebSocket:', ipad);
      // When an iPad is created or updated, reload stats to update the count
      loadStats();
    }, [loadStats]),
    accountUniqueId: account?.unique_identifier
  });

  // Fetch fresh user data when navigating to this page
  useEffect(() => {
    const fetchUserData = async () => {
      console.log('[Welcome] Fetching fresh user data...');
      try {
        const response = await axios.get('/api/v1/app/authenticated/fast/auth/me');
        if (response.data?.user) {
          console.log('[Welcome] User data fetched, supplies_viewed:', response.data.user.public_data?.supplies_viewed);
          // Update local user data which will trigger checklist rebuild
          setLocalUserData(response.data.user);
        }
      } catch (error) {
        console.error('[Welcome] Failed to fetch user data:', error);
      }
    };
    
    // Fetch when we navigate to the welcome page
    if (location.pathname === '/welcome') {
      console.log('[Welcome] Navigated to welcome page, fetching fresh user data and stats');
      fetchUserData();
      loadStats(); // Also reload stats to update service count
    }
  }, [axios, location, loadStats]); // Use full location object to catch all navigation changes

  useEffect(() => {
    // Only check for onboarding once per component mount
    if (!hasCheckedOnboarding.current && currentUser) {
      hasCheckedOnboarding.current = true;
      
      // Only show onboarding if:
      // 1. User is a marketing lead
      // 2. User needs onboarding (hasn't completed it)
      // 3. User hasn't started the onboarding yet
      const hasStartedOnboarding = currentUser?.public_data?.onboarding_started;
      
      if (currentUser?.public_data?.is_marketing_lead && 
          currentUser?.public_data?.needs_onboarding && 
          !hasStartedOnboarding) {
        setShowOnboarding(true);
      }
    }
    
    // Always load fresh stats when component mounts
    loadStats();
    
    // Reset the flag when component unmounts
    return () => {
      hasCheckedOnboarding.current = false;
    };
  }, [currentUser, loadStats]);

  // Build checklist when user, account, stats, or localUserData change
  useEffect(() => {
    buildChecklist();
  }, [currentUser, account, stats, localUserData]);

  // Set up compact header with progress
  useEffect(() => {
    const overallProgress = checklist.filter(item => item.completed).length;
    const overallTotal = checklist.length;
    const progressPercentage = overallTotal > 0 ? Math.round((overallProgress / overallTotal) * 100) : 0;
    
    const headerContent = progressPercentage < 100 ? (
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-600">{progressPercentage}% complete</span>
        <div className="w-32 bg-gray-200 rounded-full h-1.5">
          <div 
            className="bg-primary h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
    ) : null;
    
    setPageHeader('Welcome', headerContent);
    
    return () => setPageHeader(null);
  }, [checklist, setPageHeader]);

  const buildChecklist = () => {
    const items: ChecklistItem[] = [
      // Setup items
      {
        id: 'complete-profile',
        title: 'Complete your profile',
        description: 'Add your funeral home details and logo',
        icon: <Settings className="w-5 h-5" />,
        completed: !!account?.name,
        action: {
          label: 'Complete Profile',
          onClick: async () => {
            // Fetch fresh user data before showing onboarding
            try {
              const response = await axios.get('/api/v1/app/authenticated/fast/auth/me');
              if (response.data?.user) {
                setLocalUserData(response.data.user);
              }
            } catch (error) {
              console.error('[Welcome] Failed to fetch user data before onboarding:', error);
            }
            setShowOnboarding(true);
          },
          path: undefined
        },
        category: 'setup'
      },
      {
        id: 'add-ipad',
        title: 'Set up your first iPad',
        description: 'Connect an iPad to start collecting signatures',
        icon: <Tablet className="w-5 h-5" />,
        completed: stats.ipads > 0,
        action: {
          label: stats.ipads > 0 ? 'Manage iPads' : 'Add iPad',
          path: stats.ipads > 0 ? '/ipads' : '/add-ipad'
        },
        category: 'setup'
      },
      {
        id: 'buy-supplies',
        title: 'Get the right supplies',
        description: 'Stylus and optional iPad stand for best results',
        icon: <ShoppingCart className="w-5 h-5" />,
        completed: (localUserData || currentUser)?.public_data?.supplies_viewed || false,
        action: {
          label: (localUserData || currentUser)?.public_data?.supplies_viewed ? 'View Again' : 'View Recommendations',
          path: '/supplies'
        },
        category: 'setup'
      },
      
      // Service items
      {
        id: 'create-service',
        title: 'Create your first service',
        description: 'Set up a real or demo service to test',
        icon: <FileText className="w-5 h-5" />,
        completed: stats.services > 0,
        action: {
          label: stats.services > 0 ? 'View Services' : 'Create Service',
          path: stats.services > 0 ? '/services/list' : '/create-service'
        },
        category: 'service'
      },
      {
        id: 'test-signing',
        title: 'Test the signing experience',
        description: stats.services === 0 ? 'Create a service first to test signing' : 'Try signing as a guest to see how it works',
        icon: <PenTool className="w-5 h-5" />,
        completed: stats.signatures > 0,
        action: {
          label: 'Sign Service',
          onClick: stats.services > 0 ? () => setShowQRModal(true) : undefined,
          disabled: stats.services === 0
        },
        category: 'service'
      },
      {
        id: 'invite-team',
        title: 'Invite your team',
        description: 'Add staff members to help manage services',
        icon: <UserPlus className="w-5 h-5" />,
        completed: false,
        action: {
          label: 'Invite Team',
          path: '/manage-account/add-user'
        },
        category: 'service'
      },
      
      // Advanced items
      {
        id: 'email-templates',
        title: 'Set up email follow-ups',
        description: 'Automated aftercare messages to families',
        icon: <Mail className="w-5 h-5" />,
        completed: false,
        action: {
          label: 'Configure Emails',
          path: '/follow-up-messages/review'
        },
        category: 'advanced'
      }
    ];
    
    setChecklist(items);
  };

  const handleOnboardingComplete = async (data: any) => {
    setShowOnboarding(false);
    
    // Mark onboarding as complete locally so we don't need to reload
    if (currentUser) {
      currentUser.public_data = {
        ...currentUser.public_data,
        needs_onboarding: false,
        onboarding_started: true
      };
    }
    
    try {
      // Update backend
      await axios.put('/api/v1/app/authenticated/update-user-data', {
        needs_onboarding: false
      });
      
      // Refresh account data if funeral home name was set
      if (data.funeralHomeName) {
        await onLoadAccount();
      }
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }
    
    // Refresh checklist
    buildChecklist();
  };
  
  const handleOnboardingSkip = async () => {
    setShowOnboarding(false);
    
    // Mark that onboarding has been started (so modal won't show again)  
    // Also mark it locally so we don't need to reload
    if (currentUser) {
      currentUser.public_data = {
        ...currentUser.public_data,
        onboarding_started: true
      };
    }
    
    try {
      // Update backend
      await axios.put('/api/v1/app/authenticated/update-user-data', {
        onboarding_started: true
      });
      
      // Refresh account data to get any name updates (this is lightweight)
      await onLoadAccount();
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }
  };

  const setupProgress = checklist.filter(item => item.category === 'setup' && item.completed).length;
  const setupTotal = checklist.filter(item => item.category === 'setup').length;
  const overallProgress = checklist.filter(item => item.completed).length;
  const overallTotal = checklist.length;
  const progressPercentage = Math.round((overallProgress / overallTotal) * 100);

  return (
    <>
      {showOnboarding && (
        <OnboardingFlow
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
          onAccountUpdate={onLoadAccount}
          initialData={{
            funeralHomeName: account?.name,
            location: (localUserData || currentUser)?.public_data?.onboarding_data?.location,
            servicesPerMonth: (localUserData || currentUser)?.public_data?.onboarding_data?.servicesPerMonth,
            serviceTypes: (localUserData || currentUser)?.public_data?.onboarding_data?.serviceTypes,
            otherServicesText: (localUserData || currentUser)?.public_data?.onboarding_data?.otherServicesText,
            teamSize: (localUserData || currentUser)?.public_data?.onboarding_data?.teamSize,
            currentTools: (localUserData || currentUser)?.public_data?.onboarding_data?.currentTools,
            otherToolsText: (localUserData || currentUser)?.public_data?.onboarding_data?.otherToolsText,
            currentChallenges: (localUserData || currentUser)?.public_data?.onboarding_data?.currentChallenges,
            otherChallengesText: (localUserData || currentUser)?.public_data?.onboarding_data?.otherChallengesText,
            hasIPad: (localUserData || currentUser)?.public_data?.onboarding_data?.hasIPad,
            needsHelp: (localUserData || currentUser)?.public_data?.onboarding_data?.needsHelp
          }}
        />
      )}
      
      <div className="container-fixed">
        <div className="flex flex-col gap-6">

          {/* Quick Actions - moved to top with minimal spacing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card">
              <div className="card-body p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-primary-light rounded-lg">
                    <Zap className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">Quick Start</h3>
                    <p className="text-xs text-gray-600">Create a service in seconds</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/create-service')}
                  className="btn btn-primary btn-sm w-full"
                >
                  Create Service
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-body p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-info-light rounded-lg">
                    <Gift className="w-5 h-5 text-info" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">Get Supplies</h3>
                    <p className="text-xs text-gray-600">Everything you need</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/supplies')}
                  className="btn btn-info btn-sm w-full"
                >
                  Shop Supplies
                </button>
              </div>
            </div>
          </div>


          {/* Setup Checklist */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">
                {progressPercentage < 100 ? 'Setup Checklist' : 'Next Steps'}
              </h2>
              {progressPercentage < 100 && (
                <div className="badge badge-primary">
                  {overallProgress} of {overallTotal} completed
                </div>
              )}
            </div>
            <div className="card-body">
              {/* Setup section */}
              {setupTotal > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold text-gray-700 mb-4">
                    Essential Setup
                  </h3>
                  <div className="space-y-3">
                    {checklist.filter(item => item.category === 'setup').map((item) => (
                      <div
                        key={item.id}
                        className={`flex items-center justify-between p-4 rounded-xl border ${
                          item.completed 
                            ? 'bg-success-light border-success/20' 
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        } transition-colors`}
                      >
                        <div className="flex items-center gap-4">
                          {item.completed ? (
                            <CheckCircle2 className="w-6 h-6 text-success" />
                          ) : (
                            <Circle className="w-6 h-6 text-gray-400" />
                          )}
                          <div className="p-2 bg-white rounded-lg">
                            {item.icon}
                          </div>
                          <div>
                            <h4 className={`font-medium ${item.completed ? 'text-gray-600' : 'text-gray-900'}`}>
                              {item.title}
                            </h4>
                            <p className="text-sm text-gray-600">{item.description}</p>
                          </div>
                        </div>
                        
                        {item.action.path ? (
                          <Link
                            to={item.action.path}
                            className={`btn btn-sm ${item.completed ? 'btn-light' : 'btn-primary'} flex items-center gap-2`}
                          >
                            {item.completed ? 'Edit' : item.action.label}
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        ) : item.action.onClick ? (
                          <button
                            onClick={item.action.onClick}
                            className={`btn btn-sm ${item.completed ? 'btn-light' : 'btn-primary'} flex items-center gap-2`}
                          >
                            {item.completed ? 'Edit' : item.action.label}
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Service section */}
              <div className="mb-8">
                <h3 className="font-semibold text-gray-700 mb-4">
                  Start Using SignatureGuestbook
                </h3>
                <div className="space-y-3">
                  {checklist.filter(item => item.category === 'service').map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between p-4 rounded-xl border ${
                        item.completed 
                          ? 'bg-success-light border-success/20' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      } transition-colors`}
                    >
                      <div className="flex items-center gap-4">
                        {item.completed ? (
                          <CheckCircle2 className="w-6 h-6 text-success" />
                        ) : (
                          <Circle className="w-6 h-6 text-gray-400" />
                        )}
                        <div className="p-2 bg-white rounded-lg">
                          {item.icon}
                        </div>
                        <div>
                          <h4 className={`font-medium ${item.completed ? 'text-gray-600' : 'text-gray-900'}`}>
                            {item.title}
                          </h4>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                      </div>
                      
                      {item.action.path ? (
                        <Link
                          to={item.action.path}
                          className={`btn btn-sm ${item.completed ? 'btn-light' : 'btn-primary'} flex items-center gap-2`}
                        >
                          {item.action.label}
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      ) : item.action.disabled ? (
                        <button
                          disabled
                          className="btn btn-sm btn-light opacity-50 cursor-not-allowed flex items-center gap-2"
                        >
                          {item.action.label}
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={item.action.onClick}
                          className={`btn btn-sm ${item.completed ? 'btn-light' : 'btn-primary'} flex items-center gap-2`}
                        >
                          {item.action.label}
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Advanced section */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-4">
                  Level Up Your Experience
                </h3>
                <div className="space-y-3">
                  {checklist.filter(item => item.category === 'advanced').map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between p-4 rounded-xl border ${
                        item.completed 
                          ? 'bg-success-light border-success/20' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      } transition-colors`}
                    >
                      <div className="flex items-center gap-4">
                        {item.completed ? (
                          <CheckCircle2 className="w-6 h-6 text-success" />
                        ) : (
                          <Circle className="w-6 h-6 text-gray-400" />
                        )}
                        <div className="p-2 bg-white rounded-lg">
                          {item.icon}
                        </div>
                        <div>
                          <h4 className={`font-medium ${item.completed ? 'text-gray-600' : 'text-gray-900'}`}>
                            {item.title}
                          </h4>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                      </div>
                      
                      {item.action.path ? (
                        <Link
                          to={item.action.path}
                          className="btn btn-sm btn-light flex items-center gap-2"
                        >
                          {item.action.label}
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      ) : (
                        <button
                          onClick={item.action.onClick}
                          className="btn btn-sm btn-light flex items-center gap-2"
                        >
                          {item.action.label}
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="card bg-gradient-to-r from-info-light to-info/10">
            <div className="card-body p-8">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Need help getting started?</h3>
                  <p className="text-gray-600 mb-4">
                    Our team is here to help you succeed. Schedule a free onboarding call or explore our resources.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => window.open('https://link.mogulclients.com/widget/appointment/mogulclients?user_id=Dpu2AeqF9Rh1n3eDjVdU', '_blank')}
                      className="btn btn-primary"
                    >
                      Schedule Onboarding Call
                    </button>
                  </div>
                </div>
                <Book className="w-24 h-24 text-info opacity-20" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* QR Code Modal */}
      {showQRModal && firstService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Sign {firstService.deceased_name || 'Service'}</h3>
              <button
                onClick={() => setShowQRModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                Scan this QR code with your phone to test the signing experience
              </p>
              
              <div className="bg-white p-4 inline-block border rounded-lg">
                <QRCode
                  value={firstService.qr_code_sign_url || `${window.location.origin}/s/${firstService.unique_identifier}`}
                  size={200}
                />
              </div>
              
              <div className="mt-4 p-3 bg-info-light rounded-lg">
                <p className="text-sm text-info-dark">
                  <strong>Tip:</strong> This is the same QR code guests will scan at your services to sign the digital guestbook.
                </p>
              </div>
              
              <div className="mt-4 flex gap-3">
                <Link
                  to={`/services/${firstService.unique_identifier}`}
                  className="btn btn-light flex-1"
                >
                  View Service
                </Link>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="btn btn-primary flex-1"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Welcome;